import React from 'react';
import { observer } from 'mobx-react-lite';
import { Link, useLocation } from 'react-router-dom';
import { Button, ButtonProps } from '@shared/components/common/Button';
import { cn } from '@shared/utils/utils';
import { CreditCard, BookOpen, Users, User, LucideIcon } from 'lucide-react';
import { ADMIN_ROUTES } from '@/shared/configs/constants';

interface MenuItemProps extends ButtonProps {
    to?: string,
    icon?: LucideIcon,
    label: string,
    isActive?: boolean,
    children?: React.ReactNode
}

const MenuItem = ({ to, icon: Icon, label, isActive = false, children, ...props }: MenuItemProps) => {
    const baseClassName = 'w-full justify-start h-14 text-left transition-all duration-200 rounded-lg hover:bg-primary-light hover:text-white px-4';
    const activeClassName = isActive ? 'bg-primary-light text-white hover:text-white' : 'hover:text-white';

    const buttonContent = (
        <span className={cn('flex gap-3 items-center')}>
            {Icon && <Icon size={24} />}
            <span className='text-base font-medium'>{label}</span>
            {children}
        </span>
    );
    if (to) {
        return (
            <Link to={to} className='block w-full'>
                <Button
                    variant={isActive ? 'default' : 'ghost'}
                    className={cn(baseClassName, activeClassName, props.className)}
                    {...props}
                >
                    {buttonContent}
                </Button>
            </Link>
        );
    }

    return (
        <Button
            variant={isActive ? 'default' : 'ghost'}
            className={cn(baseClassName, activeClassName, props.className)}
            {...props}
        >
            {buttonContent}
        </Button>
    );
};

const AdminProfileSidebar = observer(() => {
    const { pathname } = useLocation();

    const menuItems = [
        {
            label: 'アカウント情報',
            to: ADMIN_ROUTES.profile.href,
            icon: User
        },
        {
            label: 'ユーザー定期購読一覧',
            to: ADMIN_ROUTES.userSubscriptions.href,
            icon: BookOpen
        },
        {
            label: 'ユーザー管理',
            to: ADMIN_ROUTES.userManagement.href,
            icon: Users
        },
        {
            label: 'プラン管理',
            to: ADMIN_ROUTES.planManagement.href,
            icon: CreditCard
        }
    ];

    return (
        <nav className='p-6 bg-white rounded-lg shadow-sm'>
            <h1 className='mb-8 text-3xl font-bold text-gray-900'>
                マイページ
            </h1>
            <div className='flex flex-col gap-4'>
                {menuItems.map(item => (
                    <MenuItem
                        key={item.to}
                        to={item.to}
                        icon={item.icon}
                        label={item.label}
                        isActive={pathname.includes(item.to)}
                    />
                ))}
            </div>
        </nav>
    );
});

export default AdminProfileSidebar;
